// Permiso de notificaciones push del navegador (FCM). Vivía en la pestaña
// «Alertas» del perfil; desde el 26-oct está en Configuración → Alertas.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserStore } from "../../store/userStore";
import { requestFCMToken, unsubscribeFCMToken } from "../../firebaseMessaging";

type PushStatus = "granted" | "denied" | "default" | "unsupported";

function getPushStatus(): PushStatus {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission as PushStatus;
}

interface PushNotificationCardProps {
  isDarkMode: boolean;
}

export function PushNotificationCard({ isDarkMode }: PushNotificationCardProps) {
  const { t } = useTranslation("common");
  const user = useUserStore((state) => state.user);

  const [status, setStatus] = useState<PushStatus>(getPushStatus);
  const [hasToken, setHasToken] = useState<boolean>(
    () => !!localStorage.getItem("tokenCFM"),
  );
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(false);

  const isActive = status === "granted" && hasToken;
  const showActivate =
    !isActive && status !== "denied" && status !== "unsupported";

  const textPrimary = isDarkMode ? "text-white" : "text-gray-900";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const cardBg = isDarkMode ? "bg-dark-surface" : "bg-gray-50";
  const borderColor = isDarkMode ? "border-dark-surface" : "border-gray-200";

  const handleActivate = async () => {
    if (!user?.id || status === "denied" || status === "unsupported") return;
    setWorking(true);
    setError(false);
    try {
      localStorage.removeItem("fcm_disabled_by_user");
      localStorage.removeItem("fcm_permission_denied");
      sessionStorage.removeItem("fcm_permission_toast_shown");
      const token = await requestFCMToken({ id: user.id });
      const newStatus = getPushStatus();
      const tokenOk = !!token || !!localStorage.getItem("tokenCFM");
      setStatus(newStatus);
      setHasToken(tokenOk);
      if (!(newStatus === "granted" && tokenOk)) setError(true);
    } catch {
      setError(true);
    } finally {
      setWorking(false);
    }
  };

  const handleDeactivate = async () => {
    if (!user?.id) return;
    setWorking(true);
    setError(false);
    try {
      await unsubscribeFCMToken({ id: user.id });
      setHasToken(false);
    } catch {
      setError(true);
    } finally {
      setWorking(false);
    }
  };

  const statusText = isActive
    ? t("profile.push.statusActive", "Enabled")
    : status === "denied"
      ? t("profile.push.statusBlocked", "Blocked by the browser")
      : status === "unsupported"
        ? t("profile.push.statusUnsupported", "Not supported by this browser")
        : t("profile.push.statusInactive", "Disabled");

  const description = isActive
    ? t(
        "profile.push.activeDescription",
        "You are receiving push notifications on this device.",
      )
    : status === "denied"
      ? t(
          "profile.push.blockedDescription",
          "You blocked notifications in this browser. Enable them from the site settings and try again.",
        )
      : status === "unsupported"
        ? t(
            "profile.push.unsupportedDescription",
            "This browser does not support push notifications.",
          )
        : t(
            "profile.push.inactiveDescription",
            "You don't have push notifications enabled. Turn them on to receive real-time push notifications.",
          );

  const iconClass = isActive
    ? "ri-notification-4-fill text-primary-500"
    : status === "denied" || status === "unsupported"
      ? "ri-notification-off-line text-red-500"
      : "ri-notification-off-line text-yellow-500";

  return (
    <div
      className={`${cardBg} border ${borderColor} rounded-lg p-4 flex items-center gap-4`}
    >
      <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center flex-shrink-0">
        <i className={`${iconClass} text-lg`}></i>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`${textPrimary} text-sm font-medium`}>
            {t("profile.push.title", "Real-time push notifications")}
          </p>
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${
              isActive
                ? "bg-primary-500/15 text-primary-500"
                : `bg-gray-500/15 ${textSecondary}`
            }`}
          >
            {statusText}
          </span>
        </div>
        <p className={`${textSecondary} text-xs mt-1`}>{description}</p>
        {error && (
          <p className="text-red-500 text-xs mt-1">
            {t(
              "profile.push.activateError",
              "Notifications could not be enabled. Check your browser permissions.",
            )}
          </p>
        )}
      </div>
      {(showActivate || isActive) && (
        <div className="flex-shrink-0">
          {showActivate && (
            <button
              onClick={handleActivate}
              disabled={working}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
            >
              {working && <i className="ri-loader-4-line animate-spin"></i>}
              {working
                ? t("profile.push.activating", "Enabling...")
                : t("profile.push.activate", "Enable")}
            </button>
          )}
          {isActive && (
            <button
              onClick={handleDeactivate}
              disabled={working}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
            >
              {working && <i className="ri-loader-4-line animate-spin"></i>}
              {working
                ? t("profile.push.deactivating", "Disabling...")
                : t("profile.push.deactivate", "Disable")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
